import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import { childMenuUrl, isInlineMenuGroup } from '../../utils/menu'

function MobileMenuLink({ child, onNavigate, nested = false }) {
  const { localized } = useLanguage()
  const url = childMenuUrl(child)
  if (!url) return null

  return (
    <Link
      to={url}
      onClick={onNavigate}
      className={`block rounded-lg py-3 text-sm text-text/70 hover:bg-primary/5 hover:text-primary min-h-11 flex items-center ${
        nested ? 'px-6' : 'px-4'
      }`}
    >
      {localized(child, 'title')}
    </Link>
  )
}

function MobileMenuSection({ group, onNavigate }) {
  const { localized } = useLanguage()
  const sectionUrl = childMenuUrl(group)

  return (
    <div className="py-1">
      {sectionUrl ? (
        <Link
          to={sectionUrl}
          onClick={onNavigate}
          className="block px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-primary/70"
        >
          {localized(group, 'title')}
        </Link>
      ) : (
        <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-primary/70">
          {localized(group, 'title')}
        </p>
      )}
      {group.children.map((child) => (
        <MobileMenuLink key={child.id || child.slug} child={child} onNavigate={onNavigate} nested />
      ))}
    </div>
  )
}

function MobileMenuGroup({ group, onNavigate }) {
  const { localized } = useLanguage()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-lg bg-white/60">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full min-h-11 items-center justify-between px-4 py-3 text-sm font-semibold text-primary"
        aria-expanded={expanded}
      >
        {localized(group, 'title')}
        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="pb-1">
          {group.children.map((child) => (
            <MobileMenuLink key={child.id || child.slug} child={child} onNavigate={onNavigate} nested />
          ))}
        </div>
      )}
    </div>
  )
}

export default function MobileNavItem({ item, resolveUrl, onNavigate }) {
  const { localized } = useLanguage()
  const [expanded, setExpanded] = useState(false)
  const hasChildren = item.children?.length > 0
  const itemUrl = resolveUrl(item)
  const hasLink = itemUrl != null

  if (!hasChildren) {
    if (!hasLink) return null

    return (
      <Link
        to={itemUrl}
        onClick={onNavigate}
        className="flex min-h-11 items-center rounded-xl px-4 py-3 text-base font-medium text-text/80 transition-colors hover:bg-primary/5 hover:text-primary"
      >
        {localized(item, 'title')}
      </Link>
    )
  }

  return (
    <div className="rounded-xl border border-border/40 bg-background/50">
      <div className="flex items-center justify-between">
        {hasLink ? (
          <Link
            to={itemUrl}
            onClick={onNavigate}
            className="flex-1 px-4 py-3 text-base font-semibold text-primary"
          >
            {localized(item, 'title')}
          </Link>
        ) : (
          <span className="flex-1 px-4 py-3 text-base font-semibold text-primary">
            {localized(item, 'title')}
          </span>
        )}
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="px-4 py-3 text-primary"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse submenu' : 'Expand submenu'}
        >
          <ChevronDown className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="space-y-1 border-t border-border/40 px-2 py-2">
          {item.children.map((child) =>
            isInlineMenuGroup(child) ? (
              <MobileMenuSection key={child.id || child.slug} group={child} onNavigate={onNavigate} />
            ) : child.children?.length ? (
              <MobileMenuGroup key={child.id || child.slug} group={child} onNavigate={onNavigate} />
            ) : (
              <MobileMenuLink key={child.id || child.slug} child={child} onNavigate={onNavigate} />
            )
          )}
        </div>
      )}
    </div>
  )
}
